import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import emailjs from '@emailjs/browser';
import { trackCalendlyConversion } from './GoogleTagLoader';
export function ContactSection() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({
    type: null,
    message: ''
  });
  const formRef = useRef<HTMLFormElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({
      type: null,
      message: ''
    });
    const serviceId = 'service_tssw5oa';
    const templateId = 'template_n0i17pc';
    const publicKey = 'x9SabP7t6qZW31W2T';
    emailjs.sendForm(serviceId, templateId, formRef.current!, publicKey).then(result => {
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! We will be in touch shortly to schedule your demo.'
      });
      setFormState({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        message: ''
      });
    }).catch(error => {
      console.error('EmailJS error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send. Please try again or email us directly at dan@turnout.info'
      });
    }).finally(() => {
      setIsSubmitting(false);
    });
  };
  return <section id="contact" className="py-20 bg-white" data-id="element-66">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-67">
        <AnimatedSection data-id="element-68">
          <div className="text-center mb-16" data-id="element-69">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-70">
              See What Turnout Can Do For Your Events
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-71">
              Book a 30-minute demo to see how Turnout reduces no-shows via
              personalized outreach.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12" data-id="element-72">
          <AnimatedSection delay={0.2} className="lg:col-span-2" data-id="element-73">
            <div className="bg-indigo-50 p-8 rounded-2xl border border-indigo-100" data-id="element-74">
              <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-75">
                Contact Information
              </h3>
              <div className="space-y-8" data-id="element-76">
                <div className="flex items-start" data-id="element-77">
                  <div className="bg-white p-3 rounded-lg shadow-sm mr-4" data-id="element-78">
                    <Phone className="h-6 w-6 text-indigo-600" data-id="element-79" />
                  </div>
                  <div data-id="element-80">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-81">
                      Phone
                    </p>
                    <p className="text-lg text-gray-900 font-medium" data-id="element-82">
                      630-615-9901
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-83">
                  <div className="bg-white p-3 rounded-lg shadow-sm mr-4" data-id="element-84">
                    <Mail className="h-6 w-6 text-indigo-600" data-id="element-85" />
                  </div>
                  <div data-id="element-86">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-87">
                      Email
                    </p>
                    <p className="text-lg text-gray-900 font-medium" data-id="element-88">
                      dan@turnout.info
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-89">
                  <div className="bg-white p-3 rounded-lg shadow-sm mr-4" data-id="element-90">
                    <MapPin className="h-6 w-6 text-indigo-600" data-id="element-91" />
                  </div>
                  <div data-id="element-92">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-93">
                      Address
                    </p>
                    <p className="text-lg text-gray-900 font-medium" data-id="element-94">
                      1755 University Avenue
                      <br data-id="element-95" />
                      Palo Alto, CA 94301
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.4} className="lg:col-span-3" data-id="element-96">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-10 h-full" data-id="element-97">
              <div className="mb-8 pb-8 border-b border-gray-100" data-id="element-98">
                <a href="https://calendly.com/dan-at-turnout/turnout-general-30-min-call" target="_blank" rel="noopener noreferrer" onClick={trackCalendlyConversion} className="w-full flex items-center justify-center px-6 py-4 border border-indigo-600 text-lg font-medium rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 transition-colors duration-300 group" data-id="element-99">
                  Prefer to book directly? Schedule a demo
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-100" />
                </a>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-101">
                Or Send Us a Message
              </h3>

              {submitStatus.type === 'success' && <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg border border-green-100" data-id="element-102">
                  {submitStatus.message}
                </div>}
              {submitStatus.type === 'error' && <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg border border-red-100" data-id="element-103">
                  {submitStatus.message}
                </div>}

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-104">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-105">
                  <div data-id="element-106">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-107">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" placeholder="Jane Doe" data-id="element-108" />
                  </div>
                  <div data-id="element-109">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-110">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" placeholder="jane@agency.com" data-id="element-111" />
                  </div>
                  <div data-id="element-112">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-113">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" placeholder="(555) 123-4567" data-id="element-114" />
                  </div>
                  <div data-id="element-115">
                    <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-116">
                      Events Per Month *
                    </label>
                    <select id="eventType" name="eventType" value={formState.eventType} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" data-id="element-117">
                      <option value="" data-id="element-118">
                        How many community events do you run?
                      </option>
                      <option value="1-5" data-id="element-119">1–5 events</option>
                      <option value="6-15" data-id="element-120">6–15 events</option>
                      <option value="16-30" data-id="element-121">16–30 events</option>
                      <option value="30+" data-id="element-122">30+ events</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-123">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-124">
                    What does your outreach process look like today? *
                  </label>
                  <textarea id="message" name="message" value={formState.message} onChange={handleChange} required rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" placeholder="e.g. Manual DMs, email blasts, no follow-up system..." data-id="element-125"></textarea>
                </div>
                <div data-id="element-126">
                  <button type="submit" disabled={isSubmitting} className={`w-full px-6 py-4 border border-transparent text-lg font-bold rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-300 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-md hover:-translate-y-0.5'}`} data-id="element-127">
                    {isSubmitting ? 'Sending...' : 'Request Demo'}
                  </button>
                </div>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>;
}